import classnames from "classnames";
import moment from "moment";
import Router, { SingletonRouter, useRouter, withRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IntlShape, injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getCbtToken } from "shared/cbt/cbt";
import { useFetchCbtPassengersOptions } from "shared/cbt/hooks";
import { cbtOnlyFieldsFilled } from "shared/components/SearchForm";
import { MODULES } from "shared/constants";
import {
  decrementGuests,
  incrementGuests,
  initPassengerForm,
} from "shared/data/actions/cbt";
import { getDestinationByCode } from "shared/data/actions/destinations";
import { setShouldMinimizeSearchForm } from "shared/data/actions/headerSearchForm";
import {
  setClosedHotelPropertyError,
  setHotelPassengers,
  setHotelPaxNationality,
} from "shared/data/actions/search";
import { setSearchQuery } from "shared/data/actions/storage";
import { AppState } from "shared/data/reducers";
import {
  AirportDestinations,
  DefaultAirportSuggestions,
  SearchQuery,
} from "shared/data/reducers/types/storage";
import { searchFlightBase } from "shared/lib/constants";
import {
  countPassengers,
  getBasicDate,
  isEmptyObject,
  pad2,
} from "shared/lib/functions";
import { PassengersCount } from "shared/lib/golObjectTypes/PassengerTypes";
import {
  Airlines,
  AirportSelectValue,
} from "shared/lib/golObjectTypes/SelectObjects";
import { getRoomsFromUrl } from "shared/lib/hotels/getRoomsFromUrl";
import {
  getHotelBlockSearchDaysBefore,
  getRoomTotalPeople,
  getSearchFormDefaultDepartureDays,
  hasRoomsWithOnlyGuests,
} from "shared/lib/hotels/searchForm";
import { getPriceCalendarSettings } from "shared/lib/priceCalendar";
import {
  maxPlusMinusDays,
  plusMinusDaysEnabled,
} from "shared/lib/requestorFunctions";
import { AirportDestination, FrontendSettings } from "shared/types";

import Img from "@components/FileServer/Img";
import SearchFormAirportSelect from "@components/SearchForm/AirportSelect";
import DatePicker from "@components/SearchForm/Calendar/DatePicker";
import SearchFormHotelSelect from "@components/SearchForm/HotelSelect";
import MinimizedHeaderSearchForm from "@components/SearchForm/MinimizedHeaderSearchForm";
import PassengerPreferences, {
  cbtFormFilled,
} from "@components/SearchForm/PassengerPreferences";
import FormattedMessage from "@components/UI/FormattedMessage";

import { buildIframeRedirectUrl, isIframe } from "@lib/iframe";
import { formatPassengersForQuery } from "@lib/searchQuery";
import { checkIsHotelSearchForm } from "@lib/webFunctions";
import { ROUTES } from "@shared/constants";

import DatesRow from "./DatesRow";
import FilterBar from "./FilterBar/FilterBar";
import FilterBarMobile from "./FilterBar/FilterBarMobile";

const EXPORT_HTML_PACKAGE = Boolean(process.env.NEXT_PUBLIC_EXPORT_PACKAGE);

interface HeaderSearchFormProps {
  frontendSettings: FrontendSettings;
  query?: SearchQuery;
  page?: string;
  intl?: IntlShape;
  defaultAirportSuggestions?: DefaultAirportSuggestions;
  destinations: AirportDestinations;
  departureDate?: string;
  departureDays?: number;
  durationOfStay?: number;
  currentLanguage?: string;
  router?: SingletonRouter;
  airlines?: Airlines;
  toleranceDays?: number;
  hotelsEnabled: boolean;
  anonymousSearchEnabled: boolean;
  searchQuery: SearchQuery;
  customerUsername?: string;
  carrierFilter: string[] | null;
  module: string[];
  allowedTravelerTypes: any;
  isCbtEnabled: boolean;
  isLoggedIn: boolean;
  user: any;
  defaultCountry: string;
  shouldMinimizeSearchForm: boolean;
}

interface SearchFlight {
  origin: any;
  destination: any;
  departureDate: string;
}

const getCorrectDepartureDaysNumber = (
  departureDays: number,
  blockSearchDaysBefore: number | undefined
): number => {
  if (Number.isNaN(blockSearchDaysBefore)) {
    return departureDays;
  }

  if (departureDays >= blockSearchDaysBefore) {
    return departureDays;
  }

  return blockSearchDaysBefore;
};

const appendQueryValue = (
  searchParams: URLSearchParams,
  key: string,
  value: string | string[]
) => {
  if (Array.isArray(value)) {
    value.forEach((item) => searchParams.append(key, item));
    return;
  }

  searchParams.append(key, value);
};

const getAirportCode = (airport: string | AirportSelectValue): string => {
  if (typeof airport === "string") {
    return airport;
  }

  return airport?.value || "";
};

const HeaderSearchForm = (props: HeaderSearchFormProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const inputFromRef = useRef(null);
  const inputToRef = useRef(null);
  const {
    destinations,
    defaultAirportSuggestions,
    hotelsEnabled,
    frontendSettings,
    hotelSearchSettings,
    module,
    departureDays,
    durationOfStay,
    user,
    isLoggedIn,
    isCbtEnabled,
    cbtPassengersForm,
    searchQuery,
    anonymousSearchEnabled,
    customerUsername,
    cbt,
    airlines,
    allowedTravelerTypes,
    shouldMinimizeSearchForm,
    defaultCountry,
    currentLanguage,
    toleranceDaysConfig,
    searchHotelsQuery,
    defaultDateVariant,
    searchHotelsData,
    cbtTokenFromState,
  } = useSelector((state: AppState) => ({
    destinations: state.storage.airportDestinations,
    defaultAirportSuggestions: state.storage.defaultAirportSuggestions,
    currentLanguage: state.requestorConfig.currentLanguage,
    hotelsEnabled: state.requestorConfig.hotelsEnabled,
    hotelSearchSettings: state.requestorConfig.hotelSearchSettings,
    frontendSettings: state.storage.frontendSettings,
    module: state.requestorConfig.module,
    searchHotelsQuery: state.search.searchHotels,
    departureDays: getCorrectDepartureDaysNumber(
      state.storage.departureDays,
      parseInt(
        state.storage.frontendSettings?.dealerFlightSearchSettings
          ?.blockSearchDaysBefore,
        10
      )
    ),
    durationOfStay: state.storage.durationOfStay,
    airlines: state.storage.airlines,
    user: state.user,
    isLoggedIn: state.user.isLoggedIn,
    isCbtEnabled:
      state.storage.frontendSettings.dealerCorporateSettings?.enableCbt ===
      "true",
    cbtPassengersForm: state.cbt.passengersForm,
    searchQuery: state.storage.searchQuery,
    anonymousSearchEnabled:
      state.storage.frontendSettings?.dealerCorporateSettings
        ?.enableAnonymousSearch === "true",
    customerUsername: state.user.email,
    cbt: state.cbt,
    allowedTravelerTypes: state.requestorConfig.allowedTravelerTypes,
    carrierFilter: state.storage.frontendSettings.carrierFilter,
    shouldMinimizeSearchForm: state.headerSearchForm.shouldMinimizeSearchForm,
    defaultCountry:
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      "",
    toleranceDaysConfig:
      state.storage.frontendSettings.dealerFlexShoppingSettings
        ?.maxPlusMinusDay,
    defaultDateVariant:
      state.storage.frontendSettings.dealerFlexShoppingSettings
        ?.defaultDateVariant,
    searchHotelsData: state.search.searchHotels,
    cbtTokenFromState: state.user?.cbtToken,
  }));

  const isHotelSearchForm = checkIsHotelSearchForm(router);
  const flightBlockSearchDaysBefore =
    parseInt(
      frontendSettings?.dealerFlightSearchSettings?.blockSearchDaysBefore,
      10
    ) || 0;
  const blockSearchDaysBefore = isHotelSearchForm
    ? getHotelBlockSearchDaysBefore(hotelSearchSettings)
    : flightBlockSearchDaysBefore;
  const searchFormDefaultDepartureDays = getSearchFormDefaultDepartureDays(
    isHotelSearchForm,
    departureDays,
    hotelSearchSettings
  );
  const isDefaultDateVariantAlternative = defaultDateVariant === "close";

  const [typeSearch, setTypeSearch] = useState<string>("RETURN");
  const [toleranceDays, setToleranceDays] = useState<number>(
    isDefaultDateVariantAlternative ? Number(toleranceDaysConfig) || 0 : 0
  );
  const [directFlight, setDirectFlight] = useState<boolean>(false);
  const [
    selectedTransportCompany,
    setSelectedTransportCompany,
  ] = useState<string>("all");
  const [weekends, setWeekends] = useState<boolean>(false);
  const [from, setFrom] = useState<string | AirportSelectValue>(
    defaultAirportSuggestions[0]
      ? ({
          value: defaultAirportSuggestions[0].Code,
          label: defaultAirportSuggestions[0].label,
          Country: defaultAirportSuggestions[0].Country,
          ShowCode: defaultAirportSuggestions[0].ShowCode,
          Code: defaultAirportSuggestions[0].Code,
        } as AirportSelectValue)
      : ""
  );
  const [to, setTo] = useState<string | AirportSelectValue>(
    searchFlightBase.destination
  );

  const [departureDate, setDepartureDate] = useState<string>(
    getBasicDate(searchFormDefaultDepartureDays)
  );
  const [returnDate, setReturnDate] = useState<string>(
    getBasicDate(searchFormDefaultDepartureDays + durationOfStay)
  );
  const [searchFlights, setSearchFlights] = useState<SearchFlight[]>([
    {
      ...searchFlightBase,
      departureDate: getBasicDate(departureDays),
    },
    {
      ...searchFlightBase,
      departureDate: getBasicDate(departureDays + durationOfStay),
    },
  ]);
  const [passengers, setPassengers] = useState<PassengersCount>({});
  const [rooms, setRooms] = useState<number>(1);
  const [airlinesTouched, setAirlinesTouched] = useState<boolean>(false);
  const [flightClass, setFlightClass] = useState<string>("ECO");
  const [error, setError] = useState<{
    to: boolean;
    from: boolean;
    multiCity: Record<string, any>;
    cbtPassengersForm: boolean;
  }>({ to: false, from: false, multiCity: {}, cbtPassengersForm: false });

  useEffect(() => {
    if (!isHotelSearchForm || router.query.departureDate) {
      return;
    }

    setDepartureDate(getBasicDate(searchFormDefaultDepartureDays));
    setReturnDate(
      getBasicDate(searchFormDefaultDepartureDays + durationOfStay)
    );
  }, [
    durationOfStay,
    isHotelSearchForm,
    router.query.departureDate,
    searchFormDefaultDepartureDays,
  ]);

  const getInitialPassengersNumbersFromAllowedTravelerTypes = () => {
    const oNumberOfPassengers = {};

    const isLoggedCbtUser = isCbtEnabled && isLoggedIn;

    if (isLoggedCbtUser && !isHotelSearchForm) {
      cbt.grades.forEach((grade) => {
        if (!grade.EnableForGuests) {
          return;
        }

        const count = cbtPassengersForm.guestsGradeIds.filter(
          (id) => id === grade.Id
        ).length;

        oNumberOfPassengers[`${grade.Id}_${grade.PassengerTypeCode}`] =
          count || 0;
      });
    } else {
      allowedTravelerTypes?.forEach((allowedTravelerType, index) => {
        oNumberOfPassengers[allowedTravelerType.Code] =
          index === 0 && !isLoggedCbtUser ? 1 : 0;
      });
    }

    return oNumberOfPassengers;
  };

  useFetchCbtPassengersOptions(dispatch, cbtTokenFromState, true);

  const calculateAllHotelPassengers = (): {
    totalRooms: number;
    totalPassengers: number;
  } => {
    interface Room {
      ADT: number;
      CHD: number;
      guests: number;
      childAges?: number[];
    }

    const roomEntries = Object.values(searchHotelsData.rooms || {}) as Room[];

    const totalRooms = roomEntries.length;
    const totalPassengers = roomEntries.reduce(
      (sum, room) => sum + (room.ADT || 0) + (room.CHD || 0),
      0
    );

    return {
      totalRooms,
      totalPassengers,
    };
  };

  useEffect(() => {
    const { query } = router;

    const hotelModuleEnabled = module?.includes(MODULES.HOTEL);
    const flightsModuleEnabled = module?.includes(MODULES.AIR);

    if (isHotelSearchForm) {
      const roomsFromQuery = getRoomsFromUrl(query, isCbtEnabled);
      const paxNationality = Array.isArray(query.paxNationality)
        ? query.paxNationality[0]
        : query.paxNationality || "CZ";

      if (roomsFromQuery) {
        dispatch(setHotelPassengers({ rooms: roomsFromQuery }));
      }

      if (!roomsFromQuery && !isCbtEnabled) {
        dispatch(
          setHotelPassengers({
            rooms: [{ ADT: 1, CHD: 0, childAges: [], guests: 0 }],
          })
        );
      }

      dispatch(setHotelPaxNationality(paxNationality));
    } else {
      setPassengers(getInitialPassengersNumbersFromAllowedTravelerTypes());
    }

    if (!isHotelSearchForm && !flightsModuleEnabled && hotelModuleEnabled) {
      changeTypeSearch("HOTELS");
    }

    if (isEmptyObject(searchQuery)) dispatch(setSearchQuery(query));
    if (
      router.asPath.includes(ROUTES.RESULTS) ||
      router.asPath.includes(ROUTES.RESULT) ||
      router.asPath.includes(ROUTES.HOTELS) ||
      router.asPath.includes(ROUTES.HOTEL) ||
      router.asPath.includes(ROUTES.BOOK_HOTEL)
    ) {
      parseQueryIntoSearchForm(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbt.grades.length, router.asPath]);

  const findDestinationByCode = async (code) => {
    const searchType = isHotelSearchForm ? "hotel" : "flights";
    return dispatch(getDestinationByCode(code, destinations, searchType));
  };

  const defaultValues = async (query) => {
    if (query.from) {
      const fromVal: any = { value: query.from, label: "" };
      if (query.from.includes("/")) {
        const airportCodes = query.from.split("/");
        let label = "";
        (await Promise.all(airportCodes.map(findDestinationByCode))).forEach(
          (res: AirportDestination) => {
            fromVal.Country = res?.Country;
            fromVal.ShowCode = label?.ShowCode;

            label += ` / ${res?.$t}`;
          }
        );
        fromVal.label = label.slice(3);
        fromVal.Code = query.from;
      } else {
        const label = await findDestinationByCode(query.from);

        fromVal.Country = label?.Country;
        fromVal.label = label?.$t;
        fromVal.Code = query.from;
        fromVal.ShowCode = label?.ShowCode;
      }
      const newDepartureDate = Date.parse(query.departureDate)
        ? query.departureDate
        : departureDate;

      setFrom(fromVal);
      setDepartureDate(newDepartureDate);
    }

    if (query.to) {
      const label = await findDestinationByCode(query.to);

      const newReturnDate = Date.parse(query.returnDate)
        ? query.returnDate
        : returnDate;
      if (label) {
        const toVal: any = {
          value: query.to,
          label: label?.$t,
          Country: label?.Country,
          ShowCode: label?.ShowCode,
          Code: query.to,
        };
        setTo(toVal);
        setReturnDate(newReturnDate);
      }
    }
    if (query.toleranceDays) {
      setToleranceDays(Number(query.toleranceDays));
    }

    if (query.typeSearch === "hotels") {
      const label = await findDestinationByCode(query.destination);
      if (label) {
        const destination: any = {
          value: query.destination,
          label: label?.$t,
          Country: label?.Country,
          Code: query.destination,
        };
        setTo(destination);
      }
    }
  };

  const initCbtPassengerForm = (query) => {
    const { costCenterId, travelReasonId } = query;

    if (costCenterId && travelReasonId) {
      dispatch(initPassengerForm(query));
    }
  };

  const parseQueryIntoSearchForm = async (query) => {
    if (!isHotelSearchForm) {
      initCbtPassengerForm(query);
    }

    if (query === undefined) {
      return;
    }

    const queryKeys = Object.keys(query).filter((param) =>
      param.includes("flight_")
    );
    let flightType;
    if (queryKeys.length > 0) {
      flightType = "MULTIPLE";
      const flightIndexes = Array.from(
        new Set(
          queryKeys
            .map((key) => key.match(/^flight_(\d+)_/)?.[1])
            .filter((index): index is string => Boolean(index))
        )
      ).sort((a, b) => Number(a) - Number(b));

      await parseMultiCityFlight(
        flightIndexes.map((index) => ({
          origin: getFirstQueryValue(query[`flight_${index}_origin`]),
          destination: getFirstQueryValue(query[`flight_${index}_destination`]),
          departureDate: getFirstQueryValue(
            query[`flight_${index}_departureDate`]
          ),
        }))
      );
    } else if (!query.returnDate) {
      flightType = "ONE_WAY";
      await defaultValues(query);
    } else {
      flightType = "RETURN";
      await defaultValues(query);
    }

    if (query.preferred_airline) {
      const airline = airlines.find(
        (al) => al.value === query.preferred_airline
      );
      if (airline) {
        selectAirline({ value: query.preferred_airline });
      }
    }

    setTypeSearch(flightType);
    if (query.max_transfers === "direct") {
      setDirectFlight(true);
    }
    const passengersData = getPassengers(query);
    setPassengers(passengersData.passengers);
    setDepartureDate(passengersData.departureDate);
    setReturnDate(passengersData.returnDate);
    if (query.flightClass) {
      setFlightClass(query.flightClass);
    }
    if (query.minimizeSearchForm === "true") {
      dispatch(setShouldMinimizeSearchForm(true));
    }
  };

  const getPassengers = (query) => {
    const passengersResult = {};

    const isLoggedCbtUser = isCbtEnabled && isLoggedIn;

    if (isLoggedCbtUser && !checkIsHotelSearchForm(router)) {
      cbt.grades.forEach((grade) => {
        if (!grade.EnableForGuests) {
          return;
        }

        passengersResult[`${grade.Id}_${grade.PassengerTypeCode}`] =
          Number(query[`${grade.Id}_${grade.PassengerTypeCode}`]) || 0;
      });
    } else {
      allowedTravelerTypes.forEach((allowedTravelerType) => {
        passengersResult[allowedTravelerType.Code] = query[
          allowedTravelerType.Code
        ] // Use passengersResult
          ? Number(query[allowedTravelerType.Code])
          : 0;
      });
    }

    return {
      passengers: passengersResult,
      returnDate:
        query.returnDate ||
        query.flight_2_departureDate ||
        getBasicDate(searchFormDefaultDepartureDays + durationOfStay),
      departureDate:
        query.departureDate ||
        query.flight_1_departureDate ||
        getBasicDate(searchFormDefaultDepartureDays),
    };
  };

  const parseMultiCityFlight = async (flights) => {
    const originAirports = await Promise.all(
      flights.map(({ origin }) => parseMulti(origin))
    );
    const destinationAirports = await Promise.all(
      flights.map(({ destination }) => parseMulti(destination))
    );
    const updatedSearchFlights = flights.map((flight, index) => ({
      ...(searchFlights[index] || searchFlightBase),
      origin: { ...originAirports[index] },
      destination: { ...destinationAirports[index] },
      departureDate: flight.departureDate,
    }));

    setSearchFlights(updatedSearchFlights);
  };

  const getFirstQueryValue = (value) =>
    Array.isArray(value) ? value[0] : value || "";

  const parseMulti = async (airport) => {
    const airportValue = getFirstQueryValue(airport);
    const airportObj = {
      value: "",
      label: "",
      Parent: "",
      Code: "",
    };
    if (airportValue.includes("/")) {
      const airportCodes = airportValue.split("/");
      let combinedLabel = "";

      (await Promise.all(airportCodes.map(findDestinationByCode))).forEach(
        (res) => {
          combinedLabel += ` / ${res.$t}`;
        }
      );
      airportObj.label = combinedLabel.slice(3);
    } else {
      const res = await findDestinationByCode(airportValue);
      airportObj.Parent = res?.Parent;
      airportObj.label = res?.$t;
    }

    airportObj.value = airportValue;
    airportObj.Code = airportValue;

    return airportObj;
  };

  const getQueryString = (query) => {
    const searchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === "") {
        return;
      }

      appendQueryValue(searchParams, key, value as string | string[]);
    });

    return searchParams.toString();
  };

  const getFutureAsPath = (query) => {
    if (!router.pathname.includes("[")) {
      return undefined;
    }

    const [pathWithQuery, hash] = router.asPath.split("#");
    const [path] = pathWithQuery.split("?");
    const queryString = getQueryString(query);

    return `${path}${queryString ? `?${queryString}` : ""}${
      hash ? `#${hash}` : ""
    }`;
  };

  const getQueryWithClearedDestinations = (typeSearchValue) => {
    const query = {
      ...Router.router.query,
      typeSearch: typeSearchValue === "HOTELS" ? "hotels" : "flights",
    };

    delete query.to;
    delete query.destination;

    Object.keys(query).forEach((key) => {
      if (
        key.startsWith("flight_") &&
        (typeSearchValue === "HOTELS" || key.includes("_destination"))
      ) {
        delete query[key];
      }
    });

    return query;
  };

  const clearSelectedDestinations = () => {
    setTo("");
    setSearchFlights(
      searchFlights.map((flight) => ({
        ...flight,
        destination: "",
      }))
    );
  };

  const changeTypeSearch = (typeSearchValue) => {
    if (["HOTELS", "FLIGHTS"].includes(typeSearchValue)) {
      clearSelectedDestinations();

      const query = getQueryWithClearedDestinations(typeSearchValue);
      const futureAsPath = getFutureAsPath(query);

      return Router.push(
        {
          pathname: router.pathname,
          query,
        },
        futureAsPath
      );
    }

    if (typeSearch === "MULTIPLE") {
      setTypeSearch(typeSearchValue);
      setFrom(searchFlights[0].origin);
      setTo(searchFlights[0].destination);
    } else if (typeSearchValue === "MULTIPLE") {
      const updatedSearchFlights = [...searchFlights];
      updatedSearchFlights[0] = {
        ...searchFlights[0],
        origin: from,
        destination: to,
      };
      setTypeSearch(typeSearchValue);
      setSearchFlights(updatedSearchFlights);
    } else {
      setTypeSearch(typeSearchValue);
    }
  };

  const increaseTolerance = () => {
    if (weekends) {
      return;
    }

    if (toleranceDays < maxPlusMinusDays(frontendSettings)) {
      setToleranceDays(toleranceDays + 1);
    }
  };

  const decreaseTolerance = () => {
    if (weekends) {
      return;
    }
    if (toleranceDays > 0) {
      setToleranceDays(toleranceDays - 1);
    }
  };

  const areInputsFilled = async (query) => {
    const { corporate } = cbt;

    const updatedError = { ...error };
    const allowGuestOnlyReservation = corporate?.AllowGuestOnlyReservation;

    updatedError.cbtPassengersForm =
      (await getCbtToken(user, frontendSettings, customerUsername)) &&
      !cbtFormFilled(
        cbtPassengersForm,
        allowGuestOnlyReservation,
        isHotelSearchForm,
        searchHotelsQuery.closedHotelPropertyError !== null
      );

    if (isHotelSearchForm) {
      if (to === "") {
        updatedError.to = true;
      }

      if (
        !cbtOnlyFieldsFilled(cbtPassengersForm) &&
        isCbtEnabled &&
        isLoggedIn
      ) {
        dispatch(
          setClosedHotelPropertyError(
            "SearchForm.fillInAllCbtRequiredInfoError"
          )
        );
        return false;
      }

      if (searchHotelsQuery && searchHotelsQuery.rooms) {
        const roomEntries = Object.entries(searchHotelsQuery.rooms);
        const allRoomsHavePassengers = roomEntries.every(
          ([roomIndex, room]) => {
            const roomTotal = getRoomTotalPeople(
              room,
              Number(roomIndex),
              cbtPassengersForm?.hotelRoomAssignments
            );
            return roomTotal > 0;
          }
        );

        if (!allRoomsHavePassengers) {
          dispatch(setClosedHotelPropertyError("SearchForm.oneOfRoomEmpty"));
          return false;
        }

        if (
          isCbtEnabled &&
          isLoggedIn &&
          !allowGuestOnlyReservation &&
          (await getCbtToken(user, frontendSettings, customerUsername))
        ) {
          if (
            hasRoomsWithOnlyGuests(
              searchHotelsQuery.rooms,
              cbtPassengersForm?.hotelRoomAssignments
            )
          ) {
            dispatch(
              setClosedHotelPropertyError("SearchForm.cbtGuestsOnlyNotAllowed")
            );
            return false;
          }
        }
      }

      if (
        updatedError.from ||
        updatedError.to ||
        updatedError.cbtPassengersForm
      ) {
        setError({ ...updatedError });
        return false;
      }
    } else {
      switch (typeSearch) {
        case "ONE_WAY":
        case "RETURN":
          if (from === "") {
            updatedError.from = true;
          }
          if (to === "") {
            updatedError.to = true;
          }
          if (
            updatedError.from ||
            updatedError.to ||
            updatedError.cbtPassengersForm
          ) {
            setError({ ...updatedError });
            return false;
          }
          break;
        case "MULTIPLE":
          Object.keys(query).forEach((key) => {
            if (key.includes("flight_") && query[key].length === 0) {
              updatedError.multiCity = {
                ...updatedError.multiCity,
                [key]: true,
              };
            }
          });
          if (
            Object.values(updatedError.multiCity).length > 0 ||
            updatedError.cbtPassengersForm
          ) {
            setError({
              ...updatedError,
              multiCity: { ...updatedError.multiCity },
            });
            return false;
          }
          break;
        default:
          return false;
      }
    }

    return true;
  };

  const search = async () => {
    // @ts-ignore
    if (checkIsHotelSearchForm(router)) {
      return searchHotels();
    }
    return searchFlightsFc();
  };

  const searchFlightsFc = async () => {
    const cbtToken = await getCbtToken(
      user,
      frontendSettings,
      customerUsername
    );
    if (cbtToken && cbt?.users?.length === 0) {
      return;
    }
    const formattedPassengers = formatPassengersForQuery(passengers);

    const query: any = {
      from:
        (typeSearch !== "MULTIPLE"
          ? (from as AirportSelectValue)?.value
          : "") || Router.query.from,
      to:
        (typeSearch !== "MULTIPLE" ? (to as AirportSelectValue)?.value : "") ||
        Router.query.to,
      flightClass,
      departureDate: departureDate || Router.query.departureDate,
      returnDate:
        typeSearch === "RETURN" ? returnDate || Router.query.returnDate : "",
      ...formattedPassengers,
      date: Date.now(),
    };

    if (selectedTransportCompany !== "all") {
      query.preferred_airline = selectedTransportCompany;
    }
    if (directFlight) {
      query.max_transfers = "direct";
    }
    if (toleranceDays && typeSearch !== "MULTIPLE") {
      query.toleranceDays = toleranceDays;
    }
    if (Router.query.lang) {
      query.lang = Router.query.lang;
    }
    if (Router.query.currency) {
      query.currency = Router.query.currency;
    }

    if (typeSearch === "MULTIPLE") {
      delete query.from;
      delete query.to;
      delete query.departureDate;
      delete query.returnDate;

      searchFlights.forEach((oSearchFlight, i) => {
        query[`flight_${i + 1}_origin`] = oSearchFlight.origin
          ? (oSearchFlight.origin as AirportSelectValue).value
          : "";
        query[`flight_${i + 1}_destination`] = oSearchFlight.destination
          ? (oSearchFlight.destination as AirportSelectValue).value
          : "";
        query[`flight_${i + 1}_departureDate`] = oSearchFlight.departureDate;
      });
    }

    if (!(await areInputsFilled(query))) {
      return;
    }

    dispatch(setShouldMinimizeSearchForm(true));
    redirectToSearchResults({
      searchQueryParams: query,
      pathname: ROUTES.RESULTS,
    });
  };

  const searchHotels = async () => {
    dispatch(setShouldMinimizeSearchForm(false));

    const soloTraveler = !!cbt?.corporate?.TravelArranger?.SoloTraveller;

    const hotelSearchQuery: any = {
      INF: passengers.INF || 0,
      CHD: passengers.CHD || 0,
      ADT: passengers.ADT || 0,
      guests: soloTraveler ? 0 : passengers.guests || 0,
      destination: (to as AirportSelectValue)?.value,
      departureDate: departureDate || Router.query.departureDate,
      returnDate: returnDate || Router.query.returnDate,
      typeSearch: "hotels",
      sort: "MIN_PRICE_HOTEL",
    };

    if (searchHotelsQuery && searchHotelsQuery.rooms) {
      // eslint-disable-next-line camelcase
      type RoomDataType = {
        ADT: number;
        CHD: number;
        guests: number;
        childAges: number[];
      };

      Object.entries(searchHotelsQuery.rooms).forEach(
        ([roomIndex, roomDataUntyped]) => {
          const roomData = roomDataUntyped as RoomDataType;
          if (roomData) {
            hotelSearchQuery[`room_${roomIndex}_ADT`] = roomData.ADT || 0;
            hotelSearchQuery[`room_${roomIndex}_CHD`] = roomData.CHD || 0;
            hotelSearchQuery[`room_${roomIndex}_guests`] = soloTraveler
              ? 0
              : roomData.guests || 0;

            if (Array.isArray(roomData.childAges)) {
              roomData.childAges.forEach((age: number, ageIndex: number) => {
                hotelSearchQuery[`room_${roomIndex}_CHD_age_${ageIndex}`] = age;
              });
            }
          }
        }
      );
    }

    if (searchHotelsQuery && searchHotelsQuery.paxNationality) {
      hotelSearchQuery.paxNationality = searchHotelsQuery.paxNationality;
    }

    if (searchHotelsQuery && router.query?.lang) {
      hotelSearchQuery.lang = router.query.lang;
    }

    if (Object.values(cbtPassengersForm.hotelRoomAssignments)?.length > 0) {
      Object.entries(cbtPassengersForm.hotelRoomAssignments).forEach(
        ([roomKey, roomAssignment]) => {
          const roomIndex = Number(roomKey);
          if (Array.isArray(roomAssignment)) {
            const travellerIds = roomAssignment.filter(Boolean);
            const sanitizedTravellerIds = soloTraveler
              ? travellerIds.slice(0, 1)
              : travellerIds;
            if (sanitizedTravellerIds.length > 0) {
              hotelSearchQuery[
                `room_${roomIndex}_travellers`
              ] = sanitizedTravellerIds;
            }
          } else if (roomAssignment && typeof roomAssignment === "object") {
            const travellerIds = Object.values(roomAssignment).filter(Boolean);
            const sanitizedTravellerIds = soloTraveler
              ? travellerIds.slice(0, 1)
              : travellerIds;
            if (sanitizedTravellerIds.length > 0) {
              hotelSearchQuery[
                `room_${roomIndex}_travellers`
              ] = sanitizedTravellerIds;
            }
          }
        }
      );
    }

    if (Router.query.currency) {
      hotelSearchQuery.currency = Router.query.currency;
    }

    if (!(await areInputsFilled(hotelSearchQuery))) {
      return;
    }

    hotelSearchQuery.date = Date.now();

    redirectToSearchResults({
      searchQueryParams: hotelSearchQuery,
      pathname: ROUTES.HOTELS,
    });
  };

  const redirectToSearchResults = ({ searchQueryParams, pathname }) => {
    const cbtParams = getCbtParams();

    const query = { ...searchQueryParams, ...cbtParams };

    if (isIframe(Router.router.route)) {
      top.window.location.href = buildIframeRedirectUrl(
        window.location.origin,
        pathname,
        query,
        Router.query._gl
      );
      return;
    }

    Router.push({
      pathname,
      query,
    });
  };

  const getCbtParams = () => {
    const { corporate } = cbt;
    const allowGuestOnlyReservation = corporate?.AllowGuestOnlyReservation;
    const soloTraveler = !!corporate?.TravelArranger?.SoloTraveller;

    const allKnownTravellers = Object.values(
      cbt?.users || cbt?.usersCache || {}
    );
    const currentTravellerId = allKnownTravellers.find(
      (traveller) => traveller?.Email === customerUsername
    )?.value;

    const userIds = soloTraveler
      ? currentTravellerId
        ? [currentTravellerId]
        : cbtPassengersForm.travellers.slice(0, 1)
      : cbtPassengersForm.travellers;

    const guests = soloTraveler ? 0 : cbtPassengersForm.guests;
    const guestsGradeIds = soloTraveler ? [] : cbtPassengersForm.guestsGradeIds;

    return getCbtToken(user, frontendSettings, customerUsername) &&
      cbtFormFilled(
        cbtPassengersForm,
        allowGuestOnlyReservation,
        isHotelSearchForm,
        searchHotelsQuery.closedHotelPropertyError !== null
      )
      ? {
          guestsGradeIds,
          userIds,
          guests,
          flightClass: cbtPassengersForm.flightClass,
          costCenterId: cbtPassengersForm.costCenter,
          travelReasonId: cbtPassengersForm.travelReason,
        }
      : {};
  };

  const plusCounterHandler = (type: string, id: string | null = null) => {
    if (type === "ROM") {
      setRooms(rooms + 1);
      return;
    }

    if (countPassengers(passengers) > 8) {
      return;
    }

    dispatch(incrementGuests(id));

    setPassengers((prevPassengers) => ({
      ...prevPassengers,
      [type]: (prevPassengers[type] || 0) + 1,
    }));
  };
  const minusCounterHandler = (type: string, id: string | null = null) => {
    if (type === "ROM") {
      if (rooms > 1) {
        setRooms(rooms - 1);
      }
      return;
    }

    if (!passengers[type] || passengers[type] < 1) {
      return;
    }

    dispatch(decrementGuests(id));

    setPassengers((prevPassengers) => ({
      ...prevPassengers,
      [type]: prevPassengers[type] - 1,
    }));
  };

  const setDate = (value, typeValue) => {
    if (typeValue === "DEPARTURE") {
      setDepartureDate(value);

      if (
        returnDate < value ||
        (router.query.typeSearch === "hotels" && returnDate === value)
      ) {
        if (router.query.typeSearch === "hotels") {
          const returnDateObj = new Date(value);
          returnDateObj.setDate(returnDateObj.getDate() + 1);
          const returnDateString = `${returnDateObj.getFullYear()}-${pad2(
            returnDateObj.getMonth() + 1
          )}-${pad2(returnDateObj.getDate())}`;

          setReturnDate(returnDateString);
        } else {
          setReturnDate(value);
        }
      }
    } else {
      setReturnDate(value);
    }
  };

  const setMultiCityDate = (value: string, index: number): void => {
    const updatedSearchFlights = searchFlights.map((el, i) =>
      i === index ? { ...el, departureDate: value } : el
    );

    for (let i = 0; i < updatedSearchFlights.length - 1; i++) {
      if (
        updatedSearchFlights[i].departureDate >
        updatedSearchFlights[i + 1].departureDate
      ) {
        updatedSearchFlights[i + 1].departureDate =
          updatedSearchFlights[i].departureDate;
      }
    }

    setSearchFlights(updatedSearchFlights);
  };

  const onChangeFlightClass = (value: string) => setFlightClass(value);

  const onChangeExtendedWeekends = () => {
    setWeekends(!weekends);
  };

  const onDirectFlightChange = () => {
    setDirectFlight(!directFlight);
  };

  const selectAirline = (val) => {
    setSelectedTransportCompany(val.value);
    setAirlinesTouched(val.value !== "all");
  };

  const addFlight = () => {
    if (searchFlights.length > 7) {
      return false;
    }
    setSearchFlights((prevSearchFlights) => [
      ...prevSearchFlights,
      {
        ...searchFlightBase,
        departureDate:
          prevSearchFlights[prevSearchFlights.length - 1].departureDate,
      },
    ]);
    return true;
  };

  const removeFlight = (indexSearchFlight) => {
    if (searchFlights.length <= 1) {
      return false;
    }
    setSearchFlights((prevSearchFlights) =>
      prevSearchFlights.filter((o, i) => i !== indexSearchFlight)
    );
    return true;
  };

  const { intl } = props;
  const shouldMinimizeSearchFormResolved =
    shouldMinimizeSearchForm &&
    (to || searchFlights[0].destination) &&
    !EXPORT_HTML_PACKAGE &&
    !["/iframe", "/"].includes(router.pathname);

  const priceCalendar = useMemo(() => {
    const settings = getPriceCalendarSettings(frontendSettings);
    const origin = getAirportCode(from as AirportSelectValue);
    const destination = getAirportCode(to as AirportSelectValue);

    return {
      ...settings,
      enabled:
        settings.enabled &&
        typeSearch === "RETURN" &&
        !isHotelSearchForm &&
        Boolean(origin && destination),
      origin,
      destination,
      cabin: "economy",
      maxTransfers: 2,
    };
  }, [from, frontendSettings, isHotelSearchForm, to, typeSearch]);

  const hotelModuleEnabled = module?.includes(MODULES.HOTEL);
  const flightsModuleEnabled = module?.includes(MODULES.AIR);
  const hotelsTabAvailable =
    EXPORT_HTML_PACKAGE || (hotelsEnabled && hotelModuleEnabled);

  if (router.route === "/register" && !anonymousSearchEnabled) {
    return null;
  }

  const counterSignPlusClass = classnames("counter-sign", {
    "counter-sign-disabled": searchFlights.length > 7,
  });

  const onMinimizedHeaderSearchFormButtonClick = () =>
    dispatch(setShouldMinimizeSearchForm(false));

  const onMinimizedHeaderSearchFormToLinkClick = () => {
    dispatch(setShouldMinimizeSearchForm(false));
    setTimeout(() => {
      if (typeSearch === "MULTIPLE") {
        if (searchFlights.length > 0) {
          const newSearchFlights = searchFlights.slice();
          newSearchFlights[0] = { ...newSearchFlights[0], destination: "" };

          setSearchFlights(newSearchFlights);
        }
      } else {
        setTo("");
      }
      inputToRef.current.focus();
    }, 0);
  };

  const onMinimizedHeaderSearchFormFromLinkClick = () => {
    dispatch(setShouldMinimizeSearchForm(false));
    setTimeout(() => {
      if (typeSearch === "MULTIPLE") {
        if (searchFlights.length > 0) {
          const newSearchFlights = searchFlights.slice();
          newSearchFlights[0] = { ...newSearchFlights[0], origin: "" };

          setSearchFlights(newSearchFlights);
        }
      } else {
        setFrom("");
      }
      inputFromRef.current.focus();
    }, 0);
  };

  const totalPassengersCount: () => number = () =>
    isLoggedIn && isCbtEnabled
      ? (cbtPassengersForm?.travellers?.length || 0) +
        (cbtPassengersForm?.guests || 0)
      : countPassengers(passengers);

  return (
    <div
      className={classnames("header-search-form", {
        iframe: isIframe(router.route),
      })}
    >
      {(!shouldMinimizeSearchFormResolved ||
        EXPORT_HTML_PACKAGE ||
        isIframe(props.router?.route)) && (
        <>
          <div
            className={classnames("header-search-form-tabs", {
              "header-search-form-tabs-with-hotels": hotelsTabAvailable,
            })}
          >
            {(!router.query.typeSearch ||
              router.query.typeSearch === "flights") &&
              flightsModuleEnabled && (
                <div className="header-search-form-tab-flight-container">
                  <div
                    className={classnames("header-search-form-tab", {
                      "header-search-form-tab--active":
                        typeSearch === "ONE_WAY",
                      iframe: isIframe(router?.route),
                    })}
                    style={{ overflow: "hidden" }}
                    id="header-search-form-tab_ONE_WAY"
                    onClick={() => changeTypeSearch("ONE_WAY")}
                    role="button"
                  >
                    <Img
                      src="/static/images/ico-plane-oneway.svg"
                      className={classnames("header-search-form-tab-ico", {
                        iframe: isIframe(router?.route),
                      })}
                      style={{ marginRight: "10px" }}
                      height={15}
                      width={29}
                      alt="plane icon"
                    />

                    <div
                      className={classnames(
                        "header-search-form-tab-text-ellipsis",
                        {
                          iframe: isIframe(router?.route),
                        }
                      )}
                      id="GOL_package-textStorage-SearchForm.oneWay"
                    >
                      <FormattedMessage id="SearchForm.oneWay" />
                    </div>
                  </div>
                  <div
                    className={classnames("header-search-form-tab", {
                      "header-search-form-tab--active": typeSearch === "RETURN",
                      iframe: isIframe(router?.route),
                    })}
                    style={{ overflow: "hidden" }}
                    id="header-search-form-tab_RETURN"
                    onClick={() => changeTypeSearch("RETURN")}
                    role="button"
                  >
                    <Img
                      src="/static/images/ico-plane-back.svg"
                      className={classnames(
                        "header-search-form-tab-ico-return",
                        {
                          iframe: isIframe(router?.route),
                        }
                      )}
                      style={{ marginRight: "10px" }}
                      height={22}
                      width={22}
                      alt="plane icon"
                    />

                    <div
                      className={classnames(
                        "header-search-form-tab-text-ellipsis",
                        {
                          iframe: isIframe(router?.route),
                        }
                      )}
                      id="GOL_package-textStorage-SearchForm.return"
                    >
                      <FormattedMessage id="SearchForm.return" />
                    </div>
                  </div>
                  <div
                    role="button"
                    className={classnames(
                      "header-search-form-tab",
                      { "header-search-form-tab-last": !hotelsTabAvailable },
                      {
                        "header-search-form-tab--active":
                          typeSearch === "MULTIPLE",
                        iframe: isIframe(router?.route),
                      }
                    )}
                    style={{ overflow: "hidden" }}
                    id="header-search-form-tab_MULTIPLE"
                    onClick={() => changeTypeSearch("MULTIPLE")}
                  >
                    <Img
                      src="/static/images/ico-plane-double.svg"
                      className={classnames("header-search-form-tab-ico", {
                        iframe: isIframe(router?.route),
                      })}
                      style={{ marginRight: "10px" }}
                      height={15}
                      width={36}
                      alt="plane icon"
                    />
                    <div
                      className={classnames(
                        "header-search-form-tab-text-ellipsis",
                        {
                          iframe: isIframe(router?.route),
                        }
                      )}
                      id="GOL_package-textStorage-SearchForm.multiCity"
                    >
                      <FormattedMessage id="SearchForm.multiCity" />
                    </div>
                  </div>
                </div>
              )}

            {flightsModuleEnabled && router.query?.typeSearch === "hotels" && (
              <div
                className={classnames("header-search-form-tab", {
                  iframe: isIframe(router?.route),
                })}
                style={{ overflow: "hidden" }}
                id="header-search-form-tab_RETURN"
                onClick={() => changeTypeSearch("FLIGHTS")}
                role="button"
              >
                <Img
                  src="/static/images/ico-plane-oneway.svg"
                  className={classnames("header-search-form-tab-ico", {
                    iframe: isIframe(router?.route),
                  })}
                  style={{ marginRight: "10px" }}
                  height={15}
                  width={29}
                  alt="plane icon"
                />

                <div
                  className={classnames(
                    "header-search-form-tab-text-ellipsis",
                    {
                      iframe: isIframe(router?.route),
                    }
                  )}
                >
                  <FormattedMessage id="SearchForm.flightTickets" />
                </div>
              </div>
            )}
            {hotelsTabAvailable && (
              <div
                className={classnames(
                  "header-search-form-tab header-search-form-tab--change-type-search header-search-form-tab-hotels",
                  {
                    "header-search-form-tab--active":
                      router.query.typeSearch === "hotels",
                    iframe: isIframe(router?.route),
                  }
                )}
                style={{ overflow: "hidden" }}
                role="button"
                onClick={() => changeTypeSearch("HOTELS")}
              >
                <Img
                  src="/static/images/ico-hotel.svg"
                  className={classnames("header-search-form-tab-ico", {
                    iframe: isIframe(router?.route),
                  })}
                  style={{ height: 16, marginRight: "10px" }}
                  alt="hotel icon"
                />
                <div
                  id="GOL_package-textStorage-SearchForm.hotels"
                  className={classnames(
                    "header-search-form-tab-text-ellipsis",
                    {
                      iframe: isIframe(router?.route),
                    }
                  )}
                >
                  <FormattedMessage id="SearchForm.hotels" />
                </div>
              </div>
            )}
          </div>
          <div className="header-search-form-inner">
            {(["ONE_WAY", "RETURN"].includes(typeSearch) ||
              isHotelSearchForm) && (
              <div
                className="header-search-form-inner-cols"
                id="searchForm-standard"
              >
                <div className="header-search-form-inner-first-col">
                  <div
                    className={classnames("header-search-form-desktop-line", {
                      iframe: isIframe(router?.route),
                    })}
                  >
                    <div
                      className={classnames("header-search-form-inner-field", {
                        iframe: isIframe(router?.route),
                      })}
                    >
                      <span className="header-search-form-inner-field-label">
                        {" "}
                        {isHotelSearchForm ? (
                          <FormattedMessage id="SearchForm.hotels.destinationLabel" />
                        ) : (
                          <span id="GOL_package-textStorage-SearchForm.from">
                            <FormattedMessage id="SearchForm.from" />
                          </span>
                        )}
                      </span>
                      {isHotelSearchForm ? (
                        <SearchFormHotelSelect
                          intl={intl}
                          testId="to-hotel"
                          inputRef={inputToRef}
                          defaultValue={to as AirportSelectValue}
                          changeValue={(e) => {
                            setTo(e === null ? "" : e);
                            setError({ ...error, to: false });
                          }}
                          idJumToAfterTab="to"
                          error={error.to}
                        />
                      ) : (
                        <SearchFormAirportSelect
                          intl={intl}
                          testId="from"
                          inputRef={inputFromRef}
                          defaultValue={from as AirportSelectValue}
                          allowDefaultOptions
                          changeValue={(e) => {
                            setFrom(e === null ? "" : e);
                            setError({ ...error, from: false });
                          }}
                          idJumToAfterTab="to"
                          error={error.from}
                        />
                      )}
                    </div>
                    {!isHotelSearchForm && (
                      <div
                        className={classnames(
                          "header-search-form-inner-field ",
                          {
                            iframe: isIframe(router?.route),
                          }
                        )}
                      >
                        <div className="header-search-form-inner-field-label">
                          <span id="GOL_package-textStorage-SearchForm.to">
                            <FormattedMessage id="SearchForm.to" />
                          </span>
                          {typeSearch === "RETURN" ? (
                            <div
                              id="header-search-form-multiCity-redirect-container"
                              style={{ display: "inline-block" }}
                            >
                              <span className="header-search-form-link-divider">
                                |
                              </span>
                              <span
                                id="header-search-form-multiCity-redirect"
                                onClick={() => changeTypeSearch("MULTIPLE")}
                                className="header-search-form-link-text"
                                role="button"
                              >
                                <span id="GOL_package-textStorage-SearchForm.differentReturnAirport">
                                  <FormattedMessage id="SearchForm.differentReturnAirport" />
                                </span>
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <SearchFormAirportSelect
                          intl={intl}
                          testId="to"
                          inputRef={inputToRef}
                          defaultValue={to as AirportSelectValue}
                          allowDefaultOptions={false}
                          changeValue={(e) => {
                            setTo(e === null ? "" : e);
                            setError({ ...error, to: false });
                          }}
                          error={error.to}
                        />
                      </div>
                    )}
                  </div>
                  <DatesRow
                    flightTypeSearch={typeSearch}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    setDate={setDate}
                    blockSearchDaysBefore={blockSearchDaysBefore}
                    priceCalendar={priceCalendar}
                  />
                  <div className="clearfix" />
                </div>
                <div
                  className={classnames("header-search-form-inner-second-col", {
                    "header-search-form-inner-second-col--hotels": isHotelSearchForm,
                  })}
                >
                  <PassengerPreferences
                    user={user}
                    frontendSettings={frontendSettings}
                    route={router.route}
                    error={error.cbtPassengersForm}
                    flightClass={flightClass}
                    minusCounterHandler={minusCounterHandler}
                    plusCounterHandler={plusCounterHandler}
                    onChangeFlightClass={onChangeFlightClass}
                    passengers={passengers}
                    rooms={rooms}
                    resetPassengers={() =>
                      setPassengers(
                        getInitialPassengersNumbersFromAllowedTravelerTypes()
                      )
                    }
                  />
                </div>
              </div>
            )}
            {(typeSearch === "MULTIPLE" || EXPORT_HTML_PACKAGE) &&
              !isHotelSearchForm && (
                <div id="searchForm-multiCity">
                  {searchFlights.map((oSearchFlight, indexSearchFlight) => (
                    <div
                      id={`searchForm-multiCity-row-${indexSearchFlight}`}
                      key={indexSearchFlight}
                      className={classnames("header-search-form-desktop-line", {
                        iframe: isIframe(router?.route),
                      })}
                    >
                      <div
                        className={classnames(
                          "header-search-form-inner-field header-search-form-desktop-line-one",
                          {
                            iframe: isIframe(router?.route),
                          }
                        )}
                      >
                        <span className="header-search-form-inner-field-label GOL_package-textStorage-SearchForm.from">
                          {" "}
                          <FormattedMessage id="SearchForm.from" />
                        </span>
                        <SearchFormAirportSelect
                          intl={intl}
                          inputRef={
                            indexSearchFlight === 0 ? inputFromRef : null
                          }
                          testId={`from-${indexSearchFlight}`}
                          idJumToAfterTab={`to-${indexSearchFlight}`}
                          defaultValue={
                            searchFlights[indexSearchFlight] !== undefined
                              ? (searchFlights[indexSearchFlight]
                                  .origin as AirportSelectValue)
                              : ""
                          }
                          allowDefaultOptions={indexSearchFlight === 0}
                          changeValue={(value) => {
                            const copySearchFlights = [...searchFlights];
                            copySearchFlights[indexSearchFlight].origin = value;
                            const errorAttr = `flight_${
                              indexSearchFlight + 1
                            }_origin`;
                            const updatedError = {
                              ...error,
                              multiCity: { ...error.multiCity },
                            };
                            delete updatedError.multiCity[errorAttr];
                            setSearchFlights(copySearchFlights);
                            setError({ ...updatedError });
                          }}
                          error={
                            error.multiCity[
                              `flight_${indexSearchFlight + 1}_origin`
                            ]
                          }
                        />
                      </div>
                      <div
                        className={classnames(
                          "header-search-form-inner-field header-search-form-desktop-line-one",
                          {
                            iframe: isIframe(router?.route),
                          }
                        )}
                      >
                        <span className="header-search-form-inner-field-label GOL_package-textStorage-SearchForm.to">
                          <FormattedMessage id="SearchForm.to" />
                        </span>
                        <SearchFormAirportSelect
                          intl={intl}
                          testId={`to-${indexSearchFlight}`}
                          inputRef={indexSearchFlight === 0 ? inputToRef : null}
                          defaultValue={
                            searchFlights[indexSearchFlight] !== undefined
                              ? (searchFlights[indexSearchFlight]
                                  .destination as AirportSelectValue)
                              : ""
                          }
                          allowDefaultOptions={false}
                          changeValue={(value) => {
                            const copySearchFlights = [...searchFlights];
                            copySearchFlights[
                              indexSearchFlight
                            ].destination = value;
                            const errorAttr = `flight_${
                              indexSearchFlight + 1
                            }_destination`;
                            const updatedError = {
                              ...error,
                              multiCity: { ...error.multiCity },
                            };
                            delete updatedError.multiCity[errorAttr];
                            setSearchFlights(copySearchFlights);
                            setError({ ...updatedError });
                          }}
                          error={
                            error.multiCity[
                              `flight_${indexSearchFlight + 1}_destination`
                            ]
                          }
                        />
                      </div>
                      <div
                        className={classnames(
                          "header-search-form-desktop-line-one",
                          {
                            iframe: isIframe(router?.route),
                          }
                        )}
                      >
                        <div className="header-search-form-inner-field">
                          <span className="header-search-form-inner-field-label GOL_package-textStorage-SearchForm.departureDate">
                            <FormattedMessage id="SearchForm.departureDate" />
                          </span>
                          <DatePicker
                            typeSearch="DEPARTURE"
                            testId={`departure_date_${indexSearchFlight}`}
                            date={oSearchFlight.departureDate}
                            onChange={(value) =>
                              setMultiCityDate(value, indexSearchFlight)
                            }
                            disabledTillDate={
                              searchFlights[indexSearchFlight - 1] !== undefined
                                ? new Date(
                                    searchFlights[
                                      indexSearchFlight - 1
                                    ].departureDate
                                  )
                                : moment()
                                    .add(blockSearchDaysBefore, "days")
                                    .toDate()
                            }
                          />
                        </div>
                      </div>
                      <div className="header-search-form-multiple-add">
                        {indexSearchFlight !== 0 && (
                          <div
                            role="button"
                            onClick={() => removeFlight(indexSearchFlight)}
                            className="header-search-form-multiple-add-inner-container-minus"
                          >
                            <Img
                              className="header-search-form-multiple-add-inner-image"
                              src="/static/images/ico-minus-gray.svg"
                              id={`searchForm-multiCity-button-remove-${indexSearchFlight}`}
                              alt="minus icon"
                            />
                            <span
                              id={`GOL_package-textStorage-SearchForm.remove-${indexSearchFlight}`}
                              className="header-search-form-multiple-add-minus-button-label GOL_package-textStorage-SearchForm.remove"
                            >
                              <FormattedMessage id="SearchForm.remove" />
                            </span>
                          </div>
                        )}
                        {indexSearchFlight === searchFlights.length - 1 && (
                          <div
                            role="button"
                            onClick={addFlight}
                            className="header-search-form-multiple-add-inner-container-plus"
                          >
                            <Img
                              className={`${counterSignPlusClass} header-search-form-multiple-add-inner-image`}
                              src="/static/images/ico-plus.svg"
                              id={`searchForm-multiCity-button-add-${indexSearchFlight}`}
                              alt="plus icon"
                            />
                            <span
                              id={`GOL_package-textStorage-SearchForm.anotherFlight-${indexSearchFlight}`}
                              className="header-search-form-multiple-add-plus-button-label GOL_package-textStorage-SearchForm.anotherFlight"
                            >
                              <FormattedMessage id="SearchForm.anotherFlight" />
                            </span>
                          </div>
                        )}
                      </div>
                      {indexSearchFlight !== searchFlights.length - 1 && (
                        <div className="header-search-form-multiple-divider" />
                      )}
                    </div>
                  ))}
                  <PassengerPreferences
                    multi
                    user={user}
                    frontendSettings={frontendSettings}
                    route={router.route}
                    resetPassengers={() =>
                      setPassengers(
                        getInitialPassengersNumbersFromAllowedTravelerTypes()
                      )
                    }
                    error={error.cbtPassengersForm}
                    flightClass={flightClass}
                    minusCounterHandler={minusCounterHandler}
                    plusCounterHandler={plusCounterHandler}
                    onChangeFlightClass={onChangeFlightClass}
                    passengers={passengers}
                    rooms={rooms}
                  />
                </div>
              )}
          </div>
          <FilterBar
            intl={intl}
            selectAirlines={selectAirline}
            selectedTransportCompany={selectedTransportCompany}
            search={search}
            toleranceDays={toleranceDays}
            increaseTolerance={increaseTolerance}
            decreaseTolerance={decreaseTolerance}
            weekends={weekends}
            onDirectFlightChange={onDirectFlightChange}
            extendedWeekend={onChangeExtendedWeekends}
            variableDays={
              !isHotelSearchForm &&
              typeSearch !== "MULTIPLE" &&
              plusMinusDaysEnabled(frontendSettings)
            }
            directFlights={directFlight}
            airlinesTouched={airlinesTouched}
          />

          <FilterBarMobile
            intl={intl}
            selectAirlines={selectAirline}
            selectedTransportCompany={selectedTransportCompany}
            search={search}
            toleranceDays={toleranceDays}
            increaseTolerance={increaseTolerance}
            decreaseTolerance={decreaseTolerance}
            weekends={weekends}
            onDirectFlightChange={onDirectFlightChange}
            extendedWeekend={onChangeExtendedWeekends}
            variableDays={
              !isHotelSearchForm &&
              typeSearch !== "MULTIPLE" &&
              plusMinusDaysEnabled(frontendSettings)
            }
            directFlights={directFlight}
            airlinesTouched={airlinesTouched}
          />
        </>
      )}

      {shouldMinimizeSearchFormResolved && (
        <MinimizedHeaderSearchForm
          searchFlights={searchFlights}
          from={from as AirportSelectValue}
          to={to as AirportSelectValue}
          departureDate={departureDate}
          returnDate={returnDate}
          currentLanguage={currentLanguage}
          defaultCountry={defaultCountry}
          passengersCount={
            isHotelSearchForm && !cbt?.corporate
              ? calculateAllHotelPassengers().totalPassengers
              : totalPassengersCount()
          }
          flightClass={flightClass}
          onButtonClick={onMinimizedHeaderSearchFormButtonClick}
          intl={intl}
          typeSearch={isHotelSearchForm ? "HOTELS" : typeSearch}
          onToLinkClick={onMinimizedHeaderSearchFormToLinkClick}
          onFromLinkClick={onMinimizedHeaderSearchFormFromLinkClick}
          query={router.query}
          isCbtEnabled={isCbtEnabled}
          cbtPassengersForm={cbtPassengersForm}
        />
      )}
    </div>
  );
};

export default withRouter(injectIntl(HeaderSearchForm));
